import { useState } from 'react';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import {
  Search,
  Phone,
  Play,
  Pause,
  ChevronDown,
  ChevronRight,
  Plus,
  Check,
  Flag,
  StickyNote,
  RefreshCw,
  Filter,
  X,
  AlertTriangle,
  Mic,
  Sparkles,
  MessageSquare,
  Calendar as CalendarIcon,
  Cloud,
  MoreHorizontal,
} from 'lucide-react';
import { DirectionIndicator } from '@/components/common/DirectionIndicator';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  useInteractions,
  useInteraction,
  useInteractionMessages,
  useInteractionTimeline,
  useFlagInteraction,
  useUnflagInteraction,
  useRecordingUrl,
  useInteractionEvaluation,
} from '@/hooks/use-interactions';
import { statusTone } from '@/components/interactions/call-status-tone';
import { useTasksByInteraction, useCreateTask, useCompleteTask } from '@/hooks/use-tasks';
import { cn } from '@/lib/utils';
import { formatDistanceToNow, format, subDays, addDays, startOfDay, endOfDay } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import type {
  InteractionWithMetadata,
  InteractionStatus,
  InteractionChannel,
  InteractionResponse,
  InteractionMessageResponse,
  InteractionEventResponse,
} from '@vagusx/clients';
import { getCardComponent } from '@/components/timeline/registry';
import { CallEvaluationSection } from '@/components/interactions/CallEvaluationSection';
import { CallRating } from '@/components/interactions/CallRating';

// =============================================================================
// Status Configuration
// =============================================================================

const statusConfig: Record<
  InteractionStatus,
  { label: string; dotColor: string; badgeClass: string }
> = {
  pending: {
    label: 'Pending',
    dotColor: 'bg-gray-400',
    badgeClass: 'bg-gray-100 text-gray-600',
  },
  in_progress: {
    label: 'In Progress',
    dotColor: 'bg-amber-500',
    badgeClass: 'bg-amber-100 text-amber-700',
  },
  completed: {
    label: 'Completed',
    dotColor: 'bg-green-500',
    badgeClass: 'bg-green-100 text-green-700',
  },
  failed: {
    label: 'Failed',
    dotColor: 'bg-red-500',
    badgeClass: 'bg-red-100 text-red-700',
  },
  transferred: {
    label: 'Escalated',
    dotColor: 'bg-red-500',
    badgeClass: 'bg-red-100 text-red-700',
  },
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
  transferred: 'Escalated',
};

// =============================================================================
// Date Presets for Date Range Filter
// =============================================================================

const DATE_PRESETS = [
  { label: 'Today', days: 0, direction: 'past' },
  { label: 'Next 7 days', days: 7, direction: 'future' },
  { label: 'Next 15 days', days: 15, direction: 'future' },
  { label: 'Next 30 days', days: 30, direction: 'future' },
  { label: 'Next 60 days', days: 60, direction: 'future' },
  { label: 'Last 7 days', days: 7, direction: 'past' },
  { label: 'Last 15 days', days: 15, direction: 'past' },
  { label: 'Last 30 days', days: 30, direction: 'past' },
  { label: 'Last 90 days', days: 90, direction: 'past' },
  { label: 'Custom', days: null, direction: 'past' },
] as const;

// =============================================================================
// Helper Functions
// =============================================================================

function formatDuration(seconds: number | null): string {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins === 0) return `${secs} sec`;
  if (secs === 0) return `${mins} min`;
  return `${mins} min ${secs} sec`;
}

function getPatientName(interaction: InteractionWithMetadata): string {
  if (interaction.patient) {
    const { firstName, lastName } = interaction.patient;
    const name = `${firstName ?? ''} ${lastName ?? ''}`.trim();
    return name || 'Unknown Patient';
  }
  return 'Unknown Caller';
}

function getPatientInitials(interaction: InteractionWithMetadata): string {
  if (interaction.patient) {
    const { firstName, lastName } = interaction.patient;
    return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase() || '?';
  }
  return '?';
}

// Check if message is from AI — kind = 'agent_message'
function isAiMessage(kind: string): boolean {
  return kind === 'agent_message';
}

// =============================================================================
// Activity List Item Component
// =============================================================================

interface ActivityListItemProps {
  interaction: InteractionWithMetadata;
  isSelected: boolean;
  onClick: () => void;
}

function ActivityListItem({
  interaction,
  isSelected,
  onClick,
}: ActivityListItemProps): React.ReactElement {
  const status = statusConfig[interaction.status as InteractionStatus] ?? statusConfig.pending;
  const patientName = getPatientName(interaction);
  // Prefer patient's phone (caller) over interaction.phoneNumber (inbound line)
  const displayPhone = interaction.patient?.phone ?? interaction.phoneNumber ?? '';
  const directionLabel = interaction.direction === 'inbound' ? 'Inbound' : 'Outbound';
  const channelLabel = interaction.channel === 'voice' ? 'Voice' : interaction.channel;

  // Build description from summary or channel info
  const description =
    interaction.summary?.slice(0, 100) ||
    `${directionLabel} ${channelLabel} call${displayPhone ? ` from ${displayPhone}` : ''}`;

  return (
    <div
      onClick={onClick}
      className={cn(
        'px-3 py-2.5 cursor-pointer transition-colors relative',
        isSelected ? 'bg-accent' : 'hover:bg-accent/50',
        'before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[3px] before:transition-colors',
        isSelected ? 'before:bg-primary' : 'before:bg-transparent'
      )}
    >
      {/* Row 1: Direction icon + Name + status dot + flags + time */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <DirectionIndicator
            direction={interaction.direction}
            channel={interaction.channel}
            size="sm"
          />
          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', status.dotColor)} />
          <span className="font-medium text-sm text-foreground truncate">{patientName}</span>
          {interaction.flagged === 1 && (
            <Flag className="w-3.5 h-3.5 text-destructive fill-destructive shrink-0" />
          )}
          {interaction.wasEscalated === 1 && (
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
          )}
        </div>
        <span className="text-[11px] text-muted-foreground shrink-0">
          {interaction.startedAt
            ? formatDistanceToNow(new Date(interaction.startedAt), { addSuffix: false })
            : formatDistanceToNow(new Date(interaction.createdAt), { addSuffix: false })}
        </span>
      </div>
      {/* Row 2: Description + Phone */}
      <div className="mt-1 ml-8">
        <p className="text-xs text-muted-foreground line-clamp-1">{description}</p>
        {displayPhone && (
          <span className="text-[11px] text-muted-foreground/70">{displayPhone}</span>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Call Event Card Component (Copied from health project pattern)
// =============================================================================

interface CallEventCardProps {
  interaction: InteractionResponse;
  messages: InteractionMessageResponse[] | undefined;
  isTranscriptExpanded: boolean;
  onToggleTranscript: () => void;
  patientInitials?: string;
}

function CallEventCard({
  interaction,
  messages,
  isTranscriptExpanded,
  onToggleTranscript,
  patientInitials = '?',
}: CallEventCardProps): React.ReactElement {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  const status = statusConfig[interaction.status as InteractionStatus] ?? statusConfig.pending;
  const directionLabel = interaction.direction === 'inbound' ? 'Inbound' : 'Outbound';

  const hasRecording = !!interaction.recordingUrl;
  const hasMessages = messages && messages.length > 0;
  const { data: signedRecording, isFetching: isRecordingUrlFetching } = useRecordingUrl(
    interaction.id,
    hasRecording
  );

  const togglePlay = async (): Promise<void> => {
    if (!signedRecording?.url) return;

    try {
      if (audioRef) {
        if (isPlaying) {
          audioRef.pause();
          setIsPlaying(false);
        } else {
          await audioRef.play();
          setIsPlaying(true);
        }
      } else {
        const audio = new Audio(signedRecording.url);
        audio.addEventListener('ended', () => setIsPlaying(false));
        setAudioRef(audio);
        await audio.play();
        setIsPlaying(true);
      }
    } catch {
      setIsPlaying(false);
    }
  };

  return (
    <div className="bg-card border rounded-lg p-4">
      {/* Header */}
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <DirectionIndicator
            direction={interaction.direction}
            channel={interaction.channel}
            size="sm"
          />
          <span className="font-medium text-sm text-foreground">{directionLabel} Call</span>
          {interaction.durationSeconds && (
            <span className="text-sm text-muted-foreground">
              {formatDuration(interaction.durationSeconds)}
            </span>
          )}
          <span className={cn('text-sm font-medium', status.badgeClass, 'px-2 py-0.5 rounded')}>
            {status.label}
          </span>
        </div>
        <span className="text-xs text-muted-foreground">
          {interaction.startedAt && format(new Date(interaction.startedAt), 'MMM d, h:mm a')}
        </span>
      </div>

      {/* Structured Output / Patient Responses */}
      {interaction.structuredOutput && interaction.structuredOutput.length > 0 && (
        <div className="mt-3 space-y-3">
          {interaction.structuredOutput.map((output, idx) => (
            <div key={idx} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase mb-2">
                {output.type.replace(/_/g, ' ')}
              </h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(output.data).map(([key, value]) => (
                  <span
                    key={key}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-background border border-amber-500/20 rounded text-xs"
                  >
                    {typeof value === 'boolean' && value && (
                      <Check className="w-3 h-3 text-green-600" />
                    )}
                    {typeof value === 'boolean' && !value && (
                      <X className="w-3 h-3 text-destructive" />
                    )}
                    <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                    {typeof value !== 'boolean' && value !== null && value !== undefined && (
                      <span className="font-medium text-foreground">{String(value)}</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions: Play Recording + View Transcript */}
      <div className="flex items-center gap-3 mt-4">
        {hasRecording ? (
          <Button
            size="sm"
            className="h-8 gap-2"
            onClick={togglePlay}
            disabled={!signedRecording?.url || isRecordingUrlFetching}
          >
            {isPlaying ? (
              <Pause className="h-3 w-3" fill="currentColor" />
            ) : (
              <Play className="h-3 w-3" fill="currentColor" />
            )}
            {isPlaying ? 'Pause' : 'Play Recording'}
          </Button>
        ) : (
          <Button variant="secondary" size="sm" disabled className="h-8 gap-2">
            <Play className="h-3 w-3" />
            No recording available
          </Button>
        )}

        {hasMessages && (
          <button
            className="text-sm text-muted-foreground hover:text-foreground hover:underline flex items-center gap-1 transition-colors"
            onClick={onToggleTranscript}
          >
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', isTranscriptExpanded && 'rotate-180')}
            />
            {isTranscriptExpanded ? 'Hide Transcript' : 'View Transcript'}
          </button>
        )}
      </div>

      {/* Expanded Transcript - Chat Bubble Style (text messages only; tool events via ConversationView in PR 2) */}
      {isTranscriptExpanded && hasMessages && (
        <div className="mt-4 pt-4 border-t space-y-2">
          {messages
            .filter((m) => m.kind === 'user_message' || m.kind === 'agent_message')
            .map((message) => {
              const isAgent = isAiMessage(message.kind);
              return (
                <div
                  key={message.id}
                  className={cn(
                    'group flex items-start gap-2',
                    isAgent ? 'justify-start' : 'justify-end'
                  )}
                >
                  {/* Agent icon - left side */}
                  {isAgent && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary-foreground" />
                    </div>
                  )}
                  <div
                    className={cn(
                      'max-w-[70%] rounded-lg px-3 py-1.5 text-sm relative',
                      isAgent ? 'bg-muted text-foreground' : 'bg-primary text-primary-foreground'
                    )}
                  >
                    {message.text}
                    <span
                      className={cn(
                        'absolute text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap',
                        isAgent
                          ? 'left-full ml-2 top-1/2 -translate-y-1/2'
                          : 'right-full mr-2 top-1/2 -translate-y-1/2'
                      )}
                    >
                      {format(new Date(message.timestamp), 'h:mm a')}
                    </span>
                  </div>
                  {/* Patient avatar - right side with initials */}
                  {!isAgent && (
                    <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[10px] font-medium text-primary-foreground">
                        {patientInitials}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Fallback: Show raw text if no parsed messages */}
      {isTranscriptExpanded && !hasMessages && (
        <div className="mt-4 pt-4 border-t">
          <p className="text-sm text-muted-foreground italic">No transcript messages available</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Escalation Event Card
// =============================================================================

interface EscalationCardProps {
  interaction: InteractionResponse;
}

function EscalationCard({ interaction }: EscalationCardProps): React.ReactElement | null {
  if (interaction.wasEscalated !== 1) return null;

  return (
    <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <span className="font-medium text-sm text-destructive">Escalated to Staff</span>
        </div>
        {interaction.escalatedAt && (
          <span className="text-xs text-destructive/80">
            {format(new Date(interaction.escalatedAt), 'MMM d, h:mm a')}
          </span>
        )}
      </div>
      {interaction.escalationReason && (
        <p className="text-sm text-destructive/90 mt-2">{interaction.escalationReason}</p>
      )}
    </div>
  );
}

// =============================================================================
// Timeline Component (Structure from health project)
// =============================================================================

const eventIcons: Record<string, { icon: React.ElementType; bg: string; text: string }> = {
  created: { icon: Plus, bg: 'bg-gray-200', text: 'text-gray-600' },
  call: { icon: Phone, bg: 'bg-blue-500', text: 'text-white' },
  voice: { icon: Mic, bg: 'bg-gray-800', text: 'text-white' },
  escalated: { icon: AlertTriangle, bg: 'bg-red-500', text: 'text-white' },
  completed: { icon: Check, bg: 'bg-green-500', text: 'text-white' },
  sms: { icon: MessageSquare, bg: 'bg-violet-500', text: 'text-white' },
  appointment: { icon: CalendarIcon, bg: 'bg-emerald-500', text: 'text-white' },
  ehr: { icon: Cloud, bg: 'bg-cyan-500', text: 'text-white' },
  task: { icon: StickyNote, bg: 'bg-amber-500', text: 'text-white' },
  other: { icon: Plus, bg: 'bg-gray-300', text: 'text-gray-600' },
};

interface TimelineProps {
  interaction: InteractionResponse;
  linkedTasks: Array<{ id: string; title: string; status: string; createdAt: string }> | undefined;
  patientInitials?: string;
}

function Timeline({
  interaction,
  linkedTasks,
  patientInitials,
}: TimelineProps): React.ReactElement {
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const { data: messages } = useInteractionMessages(interaction.id);
  const { data: timelineEvents } = useInteractionTimeline(interaction.id);

  const firstTask = linkedTasks?.[0];

  // Filter out call events from timeline (we show them separately with CallEventCard)
  const nonCallEvents = timelineEvents?.filter((e) => !e.eventType.startsWith('call:')) ?? [];

  return (
    <div className="space-y-4">
      {/* Task Created Event */}
      {firstTask && (
        <TimelineItem iconType="created">
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-sm text-muted-foreground">Task Created</span>
              <span className="text-xs text-muted-foreground">
                {format(new Date(firstTask.createdAt), 'MMM d, h:mm a')}
              </span>
            </div>
            <p className="text-sm text-foreground mt-1">{firstTask.title}</p>
          </div>
        </TimelineItem>
      )}

      {/* Escalation Event (if escalated, show before call) */}
      {interaction.wasEscalated === 1 && (
        <TimelineItem iconType="escalated">
          <EscalationCard interaction={interaction} />
        </TimelineItem>
      )}

      {/* Call Event */}
      <TimelineItem iconType="call">
        <CallEventCard
          interaction={interaction}
          messages={messages}
          isTranscriptExpanded={transcriptExpanded}
          onToggleTranscript={() => setTranscriptExpanded(!transcriptExpanded)}
          patientInitials={patientInitials}
        />
      </TimelineItem>

      {/* Timeline Events (SMS sent, appointment booked, etc.) */}
      {nonCallEvents.map((event) => {
        const CardComponent = getCardComponent(event.eventType);
        return (
          <TimelineItem key={event.id} iconType={event.eventType.split(':')[0] ?? 'other'}>
            <CardComponent event={event} patientInitials={patientInitials} />
          </TimelineItem>
        );
      })}

      {/* Completed Event */}
      {interaction.status === 'completed' && (
        <TimelineItem iconType="completed">
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
            <div className="flex items-baseline justify-between">
              <span className="font-medium text-sm text-green-600 dark:text-green-400">
                Call Completed
              </span>
              <span className="text-xs text-green-600/80 dark:text-green-400/80">
                {interaction.endedAt && format(new Date(interaction.endedAt), 'MMM d, h:mm a')}
              </span>
            </div>
            <p className="text-sm text-green-600/90 dark:text-green-400/90 mt-1">
              The AI assistant completed the call.
            </p>
          </div>
        </TimelineItem>
      )}
    </div>
  );
}

interface TimelineItemProps {
  iconType: string;
  children: React.ReactNode;
}

function TimelineItem({ iconType, children }: TimelineItemProps): React.ReactElement {
  const defaultIcon = { icon: Plus, bg: 'bg-gray-200', text: 'text-gray-600' };
  const iconConfig = eventIcons[iconType] ?? defaultIcon;
  const IconComponent = iconConfig.icon;

  return (
    <div className="flex gap-4">
      {/* Icon */}
      <div
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          iconConfig.bg,
          iconConfig.text
        )}
      >
        <IconComponent className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1">{children}</div>
    </div>
  );
}

// =============================================================================
// 2-Level Filter Dropdown Component
// =============================================================================

interface FilterDropdownProps {
  filters: ActivityFilters;
  setFilters: React.Dispatch<React.SetStateAction<ActivityFilters>>;
  onClose: () => void;
}

function FilterDropdown({ filters, setFilters, onClose }: FilterDropdownProps): React.ReactElement {
  const [activeSubmenu, setActiveSubmenu] = useState<'status' | 'channel' | null>(null);

  const statuses = [
    { value: 'all' as const, label: 'All Status', dot: undefined },
    { value: 'pending' as const, label: 'Pending', dot: 'bg-gray-400' },
    { value: 'in_progress' as const, label: 'In Progress', dot: 'bg-amber-500' },
    { value: 'completed' as const, label: 'Completed', dot: 'bg-green-500' },
    { value: 'failed' as const, label: 'Failed', dot: 'bg-red-500' },
    { value: 'transferred' as const, label: 'Escalated', dot: 'bg-red-500' },
  ];

  const channels = [
    { value: 'all' as const, label: 'All Channels' },
    { value: 'voice' as const, label: 'Voice' },
    { value: 'sms' as const, label: 'SMS' },
  ];

  return (
    <>
      {/* Backdrop to close on outside click */}
      <div className="fixed inset-0 z-40" onClick={onClose} />

      <div className="absolute right-0 top-full mt-1 w-48 bg-popover text-popover-foreground rounded-lg shadow-lg border z-50 overflow-hidden">
        {/* Main menu */}
        {activeSubmenu === null && (
          <div className="py-1">
            <button
              onClick={() => setActiveSubmenu('status')}
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between"
            >
              <span>Status</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={() => setActiveSubmenu('channel')}
              className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center justify-between"
            >
              <span>Channel</span>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        )}

        {/* Status submenu */}
        {activeSubmenu === 'status' && (
          <div className="py-1">
            <button
              onClick={() => setActiveSubmenu(null)}
              className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent flex items-center gap-2 border-b"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back</span>
            </button>
            {statuses.map((s) => (
              <button
                key={s.value}
                onClick={() => {
                  setFilters((f) => ({ ...f, status: s.value }));
                  onClose();
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
              >
                {s.dot && <span className={cn('w-2 h-2 rounded-full', s.dot)} />}
                {!s.dot && <span className="w-2" />}
                <span className="flex-1">{s.label}</span>
                {filters.status === s.value && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        )}

        {/* Channel submenu */}
        {activeSubmenu === 'channel' && (
          <div className="py-1">
            <button
              onClick={() => setActiveSubmenu(null)}
              className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent flex items-center gap-2 border-b"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              <span>Back</span>
            </button>
            {channels.map((c) => (
              <button
                key={c.value}
                onClick={() => {
                  setFilters((f) => ({ ...f, channel: c.value }));
                  onClose();
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
              >
                <span className="w-2" />
                <span className="flex-1">{c.label}</span>
                {filters.channel === c.value && <Check className="w-4 h-4 text-primary" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// =============================================================================
// Date Range Filter Component
// =============================================================================

interface DateRangeFilterProps {
  dateFrom?: string;
  dateTo?: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
}

function DateRangeFilter({ dateFrom, dateTo, onChange }: DateRangeFilterProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string>('Today');
  // Internal state for pending range selection (before it's complete)
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(undefined);

  // Use pending range if we're in the middle of selecting, otherwise use props
  const dateRange: DateRange | undefined = pendingRange
    ? pendingRange
    : dateFrom
      ? { from: new Date(dateFrom), to: dateTo ? new Date(dateTo) : undefined }
      : undefined;

  const handlePresetClick = (preset: (typeof DATE_PRESETS)[number]): void => {
    if (preset.days === null) {
      setSelectedPreset('Custom');
      setPendingRange(undefined); // Clear pending to allow fresh selection
      return;
    }

    let from: Date;
    let to: Date;
    if (preset.direction === 'future') {
      from = startOfDay(new Date());
      to = endOfDay(addDays(new Date(), preset.days));
    } else {
      from = startOfDay(subDays(new Date(), preset.days));
      to = endOfDay(new Date());
    }
    setSelectedPreset(preset.label);
    setPendingRange(undefined);
    onChange(from.toISOString(), to.toISOString());
    setOpen(false);
  };

  const handleCalendarSelect = (range: DateRange | undefined): void => {
    setSelectedPreset('Custom');
    if (range?.from && range?.to) {
      // Range is complete - commit to parent and close
      setPendingRange(undefined);
      onChange(startOfDay(range.from).toISOString(), endOfDay(range.to).toISOString());
      setOpen(false);
    } else if (range?.from) {
      // First date selected - store in pending state, keep popover open
      setPendingRange({ from: range.from, to: undefined });
    } else {
      // Cleared
      setPendingRange(undefined);
    }
  };

  const handleClear = (): void => {
    setSelectedPreset('');
    setPendingRange(undefined);
    onChange(undefined, undefined);
    setOpen(false);
  };

  // Reset pending state when popover closes without completing selection
  const handleOpenChange = (isOpen: boolean): void => {
    setOpen(isOpen);
    if (!isOpen) {
      setPendingRange(undefined);
    }
  };

  const getDisplayText = (): string => {
    if (!dateFrom && !pendingRange?.from) return 'Date';
    const from = pendingRange?.from ?? (dateFrom ? new Date(dateFrom) : undefined);
    const to = pendingRange?.to ?? (dateTo ? new Date(dateTo) : undefined);
    if (!from) return 'Date';
    if (!to) return format(from, 'MMM d') + '...';
    const fromStr = format(from, 'MMM d');
    const toStr = format(to, 'MMM d');
    return fromStr === toStr ? fromStr : `${fromStr} - ${toStr}`;
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'p-1.5 rounded-lg hover:bg-accent transition-colors relative',
            dateFrom && dateTo ? 'text-primary bg-primary/10' : 'text-muted-foreground'
          )}
          title={getDisplayText()}
        >
          <CalendarIcon className="w-4 h-4" />
          {dateFrom && dateTo && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0 overflow-hidden">
        {/* Header with current selection and clear */}
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="text-sm font-medium">{getDisplayText()}</span>
          {dateFrom && dateTo && (
            <button
              onClick={handleClear}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex overflow-hidden">
          {/* Presets sidebar */}
          <div className="border-r p-2 w-36 shrink-0 max-h-[320px] overflow-y-auto space-y-1">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  'w-full px-2 py-1.5 text-left text-sm rounded-md transition-colors',
                  selectedPreset === preset.label
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50'
                )}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {/* Dual calendar */}
          <div className="p-2 overflow-hidden">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleCalendarSelect}
              numberOfMonths={2}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Detail Panel Component
// =============================================================================

interface DetailPanelProps {
  interaction: InteractionResponse;
  interactionMeta: InteractionWithMetadata | undefined;
}

function DetailPanel({ interaction, interactionMeta }: DetailPanelProps): React.ReactElement {
  const { data: linkedTasks } = useTasksByInteraction(interaction.id);
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const flagInteraction = useFlagInteraction();
  const unflagInteraction = useUnflagInteraction();

  const patientName = interactionMeta ? getPatientName(interactionMeta) : 'Unknown Caller';
  const patientInitials = interactionMeta ? getPatientInitials(interactionMeta) : '?';
  // For inbound calls, show patient's phone (caller) instead of the inbound line (called number)
  const displayPhone = interactionMeta?.patient?.phone ?? interaction.phoneNumber ?? '';
  const status = statusConfig[interaction.status as InteractionStatus] ?? statusConfig.pending;
  const isEscalated = interaction.wasEscalated === 1;
  const isFlagged = interaction.flagged === 1;

  // Same query key `CallEvaluationSection` uses further down this page, so
  // TanStack serves it from cache — this is not a second request.
  const { data: evaluation } = useInteractionEvaluation(interaction.id);
  const evaluationTone = statusTone(evaluation?.status);
  const firstTask = linkedTasks?.[0];

  const handleCreateTask = (): void => {
    createTask.mutate({
      patientId: interaction.patientId ?? undefined,
      title: `Follow-up for call on ${format(new Date(interaction.createdAt), 'MMM d')}`,
      priority: 'medium',
    });
  };

  const handleMarkComplete = (): void => {
    if (firstTask) {
      completeTask.mutate(firstTask.id);
    }
  };

  const handleFlagToggle = (): void => {
    if (isFlagged) {
      unflagInteraction.mutate(interaction.id);
    } else {
      flagInteraction.mutate(interaction.id);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Escalation Warning Banner */}
      {isEscalated && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-5 py-2.5 flex items-center gap-2 shrink-0">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
          <span className="text-sm text-destructive flex-1">
            <span className="font-medium">Escalated</span>
            {interaction.escalationReason && ` - ${interaction.escalationReason}`}
          </span>
        </div>
      )}

      {/* Patient Header */}
      <div className="bg-card border-b px-5 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* The initials carry the call's traffic light: green / yellow /
                red for all_clear / follow_up / needs_you.

                Escalation still wins — it is a human-flagged state and outranks
                a computed one. A call with no evaluation stays neutral grey
                rather than green: most historical and every non-outbound call
                has no evaluation row, and a call we never judged must not
                present as clean. */}
            <div
              className={cn(
                'w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium',
                isEscalated
                  ? 'bg-destructive/10 ring-2 ring-destructive/30 text-destructive'
                  : (evaluationTone?.avatar ?? 'bg-muted text-muted-foreground')
              )}
              // Colour is the only signal here, so it needs a text equivalent.
              title={isEscalated ? 'Escalated' : evaluationTone?.label}
              aria-label={isEscalated ? 'Escalated' : evaluationTone?.label}
            >
              {patientInitials}
            </div>
            <div>
              <div className="flex items-center gap-2">
                {interactionMeta?.patient ? (
                  <Link
                    to="/patients/$id"
                    params={{ id: interactionMeta.patient.id }}
                    className="text-sm font-semibold text-foreground hover:text-primary hover:underline"
                  >
                    {patientName}
                  </Link>
                ) : (
                  <h2 className="text-sm font-semibold text-foreground">{patientName}</h2>
                )}
                {isFlagged && <Flag className="w-3.5 h-3.5 text-destructive fill-destructive" />}
              </div>
              <p className="text-xs text-muted-foreground">
                {displayPhone}
                {interaction.agentName && ` • ${interaction.agentName}`}
                {interaction.durationSeconds && ` • ${formatDuration(interaction.durationSeconds)}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={cn('px-2 py-0.5 rounded text-xs font-medium', status.badgeClass)}>
              {status.label}
            </span>
            {isEscalated && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-destructive/10 text-destructive">
                Escalated
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 hover:bg-accent rounded-lg text-muted-foreground">
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handleFlagToggle}
                  disabled={flagInteraction.isPending || unflagInteraction.isPending}
                  className={cn(isFlagged ? 'text-foreground' : 'text-destructive')}
                >
                  <Flag className={cn('w-4 h-4 mr-2', isFlagged && 'fill-current')} />
                  {isFlagged ? 'Unflag' : 'Flag'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {/* Summary Section */}
        {interaction.summary && (
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Summary
            </h3>
            <div className="bg-card border rounded-lg p-4">
              <p className="text-sm text-foreground leading-relaxed">{interaction.summary}</p>
            </div>
          </div>
        )}

        {/* What the CALLER said the call was worth (ADR-200). Above the
            evaluation on purpose: a person's own verdict outranks the model's,
            and it renders nothing at all when there is no rating. */}
        <CallRating rating={interaction.callRating ?? null} className="mb-6" />

        {/* Call Evaluation (post-call LLM verdict) */}
        <CallEvaluationSection interactionId={interaction.id} />

        {/* Activity Timeline */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            Activity
          </h3>
          <Timeline
            interaction={interaction}
            linkedTasks={linkedTasks}
            patientInitials={patientInitials}
          />
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="bg-card border-t px-5 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 gap-1.5">
              <StickyNote className="h-3.5 w-3.5" />
              Add Note
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {!firstTask && interaction.status === 'completed' && (
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={handleCreateTask}
                disabled={createTask.isPending}
              >
                Create Task
              </Button>
            )}
            {firstTask && firstTask.status !== 'completed' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-destructive hover:text-destructive"
                >
                  Escalate
                </Button>
                <Button
                  size="sm"
                  className="h-8 gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleMarkComplete}
                  disabled={completeTask.isPending}
                >
                  <Check className="h-3.5 w-3.5" />
                  Mark Done
                </Button>
              </>
            )}
            {interaction.status === 'completed' && !firstTask && (
              <span className="flex items-center gap-1.5 text-sm text-green-600">
                <Check className="h-4 w-4" />
                Completed
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Empty State Component
// =============================================================================

function EmptyState(): React.ReactElement {
  return (
    <div className="flex-1 flex items-center justify-center text-muted-foreground bg-background">
      Select an activity to view details
    </div>
  );
}

// =============================================================================
// Main Activity Page
// =============================================================================

interface ActivityFilters {
  search: string;
  status: InteractionStatus | 'all';
  channel: InteractionChannel | 'all';
  dateFrom?: string;
  dateTo?: string;
}

export default function ActivityPage(): React.ReactElement {
  const { interactionId: interactionIdParam } = useParams({ strict: false }) as {
    interactionId?: string;
  };
  const navigate = useNavigate();

  // Initialize with Today as default
  const [filters, setFilters] = useState<ActivityFilters>(() => {
    const from = startOfDay(new Date());
    const to = endOfDay(new Date());
    return {
      search: '',
      status: 'all',
      channel: 'all',
      dateFrom: from.toISOString(),
      dateTo: to.toISOString(),
    };
  });
  const [refreshing, setRefreshing] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Fetch interactions with metadata
  const {
    data: interactionsResponse,
    isLoading,
    refetch,
  } = useInteractions({
    status: filters.status !== 'all' ? filters.status : undefined,
    channel: filters.channel !== 'all' ? filters.channel : undefined,
    search: filters.search || undefined,
    startDate: filters.dateFrom,
    endDate: filters.dateTo,
    limit: 100,
  });

  const interactions = interactionsResponse?.items ?? [];

  // Get the selected interaction details
  const selectedInteractionId =
    interactionIdParam ?? (interactions.length > 0 ? interactions[0]?.id : undefined);

  const { data: selectedInteraction, isLoading: interactionLoading } =
    useInteraction(selectedInteractionId);

  const selectedInteractionMeta = interactions.find((i) => i.id === selectedInteractionId);

  // Handle interaction selection
  const handleSelectInteraction = (id: string): void => {
    navigate({ to: '/activity/$interactionId', params: { interactionId: id }, replace: true });
  };

  // Handle refresh
  const handleRefresh = async (): Promise<void> => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  // Filter interactions client-side for search
  const filteredInteractions = filters.search
    ? interactions.filter((interaction) => {
        const searchLower = filters.search.toLowerCase();
        const matchesPhone = interaction.phoneNumber?.toLowerCase().includes(searchLower);
        const matchesSummary = interaction.summary?.toLowerCase().includes(searchLower);
        const matchesPatient =
          interaction.patient?.firstName?.toLowerCase().includes(searchLower) ||
          interaction.patient?.lastName?.toLowerCase().includes(searchLower);
        return matchesPhone || matchesSummary || matchesPatient;
      })
    : interactions;

  const hasActiveFilters = filters.status !== 'all' || filters.channel !== 'all';
  const hasDateFilter = !!filters.dateFrom && !!filters.dateTo;

  return (
    // `h-full min-h-0` instead of `h-[calc(100vh-0px)]`: the latter ignored
    // the impersonation banner above the route shell and pushed the bottom of
    // this container below the viewport, clipping the interaction-count footer
    // in the left list. With `h-full min-h-0` the page absorbs whatever height
    // its layout parent (the authenticated-route container) gives it.
    <div className="flex h-full min-h-0 bg-background overflow-hidden">
      {/* Left Sidebar - Activity List */}
      <aside className="w-80 bg-card border-r flex flex-col shrink-0 min-h-0">
        {/* Search Header */}
        <div className="px-3 py-3 border-b">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search patients..."
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                className="w-full pl-8 pr-3 py-1.5 bg-secondary border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
              />
            </div>
            <button
              onClick={handleRefresh}
              className={cn(
                'p-1.5 hover:bg-accent rounded-lg text-muted-foreground',
                refreshing && 'animate-spin'
              )}
            >
              <RefreshCw className="w-4 h-4" />
            </button>

            {/* Date Range Filter */}
            <DateRangeFilter
              dateFrom={filters.dateFrom}
              dateTo={filters.dateTo}
              onChange={(from, to) => setFilters((f) => ({ ...f, dateFrom: from, dateTo: to }))}
            />

            {/* 2-Level Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className={cn(
                  'p-1.5 hover:bg-accent rounded-lg relative',
                  hasActiveFilters ? 'text-primary bg-primary/10' : 'text-muted-foreground'
                )}
              >
                <Filter className="w-4 h-4" />
                {hasActiveFilters && (
                  <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
                )}
              </button>
              {showFilterDropdown && (
                <FilterDropdown
                  filters={filters}
                  setFilters={setFilters}
                  onClose={() => setShowFilterDropdown(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Active Filters */}
        {(hasActiveFilters || hasDateFilter) && (
          <div className="px-3 py-2 border-b flex items-center gap-2 flex-wrap">
            {/* Date filter badge */}
            {filters.dateFrom && filters.dateTo && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                <CalendarIcon className="w-3 h-3" />
                {(() => {
                  const fromStr = format(new Date(filters.dateFrom), 'MMM d');
                  const toStr = format(new Date(filters.dateTo), 'MMM d');
                  return fromStr === toStr ? fromStr : `${fromStr} - ${toStr}`;
                })()}
                <button
                  onClick={() =>
                    setFilters((f) => ({ ...f, dateFrom: undefined, dateTo: undefined }))
                  }
                  className="hover:text-primary/80"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.status !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                {statusLabels[filters.status]}
                <button
                  onClick={() => setFilters((f) => ({ ...f, status: 'all' }))}
                  className="hover:text-primary/80"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters.channel !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs">
                {filters.channel}
                <button
                  onClick={() => setFilters((f) => ({ ...f, channel: 'all' }))}
                  className="hover:text-primary/80"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {(hasActiveFilters || hasDateFilter) &&
              (filters.status !== 'all' ||
                filters.channel !== 'all' ||
                (filters.dateFrom && filters.dateTo)) && (
                <button
                  onClick={() =>
                    setFilters({
                      search: '',
                      status: 'all',
                      channel: 'all',
                      dateFrom: undefined,
                      dateTo: undefined,
                    })
                  }
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear all
                </button>
              )}
          </div>
        )}

        {/* Activity List */}
        {/* `min-h-0` is load-bearing: without it the flex item's
            `min-height: auto` lets the ScrollArea grow to fit all N
            interactions, so Radix's `size-full` viewport never clips and
            no scrollbar appears. Same gotcha as TaskListPanel. */}
        <ScrollArea className="min-h-0 flex-1">
          {isLoading ? (
            <div className="p-3 space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredInteractions.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No activity found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredInteractions.map((interaction) => (
                <ActivityListItem
                  key={interaction.id}
                  interaction={interaction}
                  isSelected={interaction.id === selectedInteractionId}
                  onClick={() => handleSelectInteraction(interaction.id)}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Count */}
        <div className="shrink-0 border-t px-3 py-2">
          <span className="text-xs text-muted-foreground">
            {filteredInteractions.length} interaction{filteredInteractions.length !== 1 ? 's' : ''}
          </span>
        </div>
      </aside>

      {/* Right Panel - Detail */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {interactionLoading ? (
          <div className="p-6 space-y-4 bg-background flex-1">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : selectedInteraction ? (
          <DetailPanel
            interaction={selectedInteraction}
            interactionMeta={selectedInteractionMeta}
          />
        ) : (
          <EmptyState />
        )}
      </main>
    </div>
  );
}
